// Viewbooks.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// Import pdf.js (critical: use legacy build for webpack/vite)
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.js";
import "pdfjs-dist/legacy/build/pdf.worker.entry.js";

export default function Viewbooks() {
  const location = useLocation();
  const [fileUrl, setFileUrl] = useState("");
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.3);
  const [isLoading, setIsLoading] = useState(true);

  const canvasRef = useRef(null);
  const cache = useRef(new Map()); // Stores pre-rendered canvases

  // Get URL and redirect if missing
  useEffect(() => {
    const url = location.state?.url || new URLSearchParams(window.location.search).get("url") || "";
    if (!url) return (window.location.href = "/book");
    setFileUrl(url);
  }, [location]);

  // Load PDF once
  useEffect(() => {
    if (!fileUrl) return;

    const loadingTask = pdfjsLib.getDocument(fileUrl);
    loadingTask.promise
      .then((loadedPdf) => {
        setPdf(loadedPdf);
        setNumPages(loadedPdf.numPages);
        setIsLoading(false);
        renderPage(1); // First page
        preloadPages(1, loadedPdf);
      })
      .catch((err) => {
        console.error("PDF load failed:", err);
        setIsLoading(false);
      });

  }, [fileUrl]);

  // Preload pages around current page (±3)
  const preloadPages = async (current, pdfDoc) => {
    const range = 3;
    for (let i = Math.max(1, current - range); i <= Math.min(numPages, current + range); i++) {
      if (!cache.current.has(i)) {
        try {
          const page = await pdfDoc.getPage(i);
          const viewport = page.getViewport({ scale });
          const offscreen = document.createElement("canvas");
          offscreen.width = viewport.width;
          offscreen.height = viewport.height;
          const ctx = offscreen.getContext("2d");
          await page.render({ canvasContext: ctx, viewport }).promise;
          cache.current.set(i, offscreen);
        } catch (err) {
          console.warn(`Preload failed for page ${i}`, err);
        }
      }
    }
  };

  // Render current page — INSTANT if cached
  const renderPage = async (num) => {
    if (!pdf || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Use cached version if exists → 0ms render
    const cachedCanvas = cache.current.get(num);
    if (cachedCanvas) {
      canvas.width = cachedCanvas.width;
      canvas.height = cachedCanvas.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(cachedCanvas, 0, 0);
      return;
    }

    // Fallback: render live (only first time)
    try {
      const page = await pdf.getPage(num);
      const viewport = page.getViewport({ scale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;
    } catch (err) {
      console.error("Render failed:", err);
    }
  };

  // Navigation — feels INSTANT
  const goToPage = (num) => {
    if (num < 1 || num > numPages || num === pageNumber) return;
    setPageNumber(num);
    renderPage(num);
    preloadPages(num, pdf);
  };

  const nextPage = () => goToPage(pageNumber + 1);
  const prevPage = () => goToPage(pageNumber - 1);

  // Re-render on scale change
  useEffect(() => {
    if (pdf) {
      cache.current.clear(); // Clear cache when zoom changes
      renderPage(pageNumber);
      preloadPages(pageNumber, pdf);
    }
  }, [scale, pageNumber, pdf]);

  // Zoom
  const zoomIn = () => setScale((s) => Math.min(s + 0.2, 3));
  const zoomOut = () => setScale((s) => Math.max(s - 0.2, 0.5));

  if (!fileUrl || isLoading) {
    return (
      <div className="bg-black min-h-screen flex items-center justify-center">
        <div className="text-emerald-400 text-2xl">Loading PDF...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-emerald-100 flex flex-col items-center p-2 sm:p-4">
      <div className="w-full max-w-5xl mx-auto">
        {/* Top Controls */}
        <div className="sticky top-0 z-10 mb-4 sm:mb-6 w-full">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 rounded-xl border border-emerald-800/40 bg-black/90 px-3 py-2 sm:px-4 sm:py-3 backdrop-blur supports-[backdrop-filter]:bg-black/40 text-emerald-300">

            {/* Navigation Buttons - Mobile: Full width, Desktop: Auto */}
            <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
              <button
                onClick={prevPage}
                disabled={pageNumber <= 1}
                className="h-9 sm:h-10 px-3 sm:px-4 text-sm sm:text-base bg-emerald-600 rounded-full disabled:opacity-40 hover:bg-emerald-500 transition focus:outline-none text-white"
              >
                <span className="hidden sm:inline">← Prev</span>
                <span className="sm:hidden">←</span>
              </button>

              {/* Page Counter - Centered on mobile */}
              <div className="flex items-center gap-2 bg-black/50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-emerald-800/40">
                <span className="font-medium text-xs sm:text-sm">Page</span>
                <input
                  type="number"
                  value={pageNumber}
                  onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                  min="1"
                  max={numPages}
                  className="w-10 sm:w-16 bg-transparent text-center outline-none text-sm sm:text-base"
                />
                <span className="text-xs sm:text-sm">of {numPages}</span>
              </div>

              <button
                onClick={nextPage}
                disabled={pageNumber >= numPages}
                className="h-9 sm:h-10 px-3 sm:px-4 text-sm sm:text-base bg-emerald-600 rounded-full disabled:opacity-40 hover:bg-emerald-500 transition focus:outline-none text-white"
              >
                <span className="hidden sm:inline">Next →</span>
                <span className="sm:hidden">→</span>
              </button>
            </div>

            {/* Zoom Controls - Mobile: Full width, Desktop: Auto */}
            <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
              <button
                onClick={zoomOut}
                className="h-8 w-8 sm:h-10 sm:w-10 grid place-items-center bg-emerald-700 rounded-full hover:bg-emerald-600 transition focus:outline-none text-lg sm:text-xl"
              >
                −
              </button>
              <span className="w-14 sm:w-16 text-center text-xs sm:text-sm">{Math.round(scale * 100)}%</span>
              <button
                onClick={zoomIn}
                className="h-8 w-8 sm:h-10 sm:w-10 grid place-items-center bg-emerald-700 rounded-full hover:bg-emerald-600 transition focus:outline-none text-lg sm:text-xl"
              >
                +
              </button>
            </div>
          </div>
        </div>


        {/* Canvas Viewer */}
        <div className="relative rounded-lg sm:rounded-xl shadow-2xl overflow-hidden border border-emerald-800/40">
          <canvas
            ref={canvasRef}
            className="mx-auto block max-w-full"
            style={{ imageRendering: "crisp-edges" }}
          />
          {/* Side Arrows - Smaller on mobile */}
          <button
            onClick={prevPage}
            disabled={pageNumber <= 1}
            className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 grid place-items-center rounded-full bg-black/50 text-emerald-100 hover:bg-black/60 border border-emerald-700/50 shadow-lg backdrop-blur disabled:opacity-40 text-sm sm:text-base"
            aria-label="Previous page"
          >
            ←
          </button>
          <button
            onClick={nextPage}
            disabled={pageNumber >= numPages}
            className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 grid place-items-center rounded-full bg-black/50 text-emerald-100 hover:bg-black/60 border border-emerald-700/50 shadow-lg backdrop-blur disabled:opacity-40 text-sm sm:text-base"
            aria-label="Next page"
          >
            →
          </button>
        </div>

        {/* Bottom Page Numbers - Hidden on mobile, visible on tablet+ */}
        {numPages > 1 && (
          <div className="mt-4 sm:mt-8 w-full overflow-x-auto hidden sm:block">
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-2">
                <button
                  onClick={() => goToPage(1)}
                  disabled={pageNumber === 1}
                  className="h-9 px-3 rounded-full bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 transition"
                >
                  « First
                </button>
                <button
                  onClick={prevPage}
                  disabled={pageNumber === 1}
                  className="h-9 px-3 rounded-full bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 transition"
                >
                  ← Prev
                </button>

                {Array.from({ length: Math.min(10, numPages) }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => goToPage(p)}
                    className={`h-9 px-3 rounded-full transition ${p === pageNumber
                      ? "bg-emerald-600 text-white shadow-lg"
                      : "bg-emerald-900/60 hover:bg-emerald-800"
                      }`}
                  >
                    {p}
                  </button>
                ))}
                {numPages > 10 && (
                  <span className="px-3 py-1 text-emerald-400">… {numPages}</span>
                )}

                <button
                  onClick={nextPage}
                  disabled={pageNumber === numPages}
                  className="h-9 px-3 rounded-full bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 transition"
                >
                  Next →
                </button>
                <button
                  onClick={() => goToPage(numPages)}
                  disabled={pageNumber === numPages}
                  className="h-9 px-3 rounded-full bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 transition"
                >
                  Last »
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}